import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { searchIndex, bestExactHit, type ScoredHit } from "@/lib/search-index";

interface Props {
  variant?: "desktop" | "mobile";
  autoFocusRef?: React.MutableRefObject<HTMLInputElement | null>;
  placeholder?: string;
  onNavigate?: () => void;
}

export function SearchAutocomplete({ variant = "desktop", autoFocusRef, placeholder, onNavigate }: Props) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const localRef = useRef<HTMLInputElement | null>(null);
  const inputRef = autoFocusRef ?? localRef;
  const navigate = useNavigate();

  const hits: ScoredHit[] = useMemo(() => (query.trim().length >= 2 ? searchIndex(query, 8) : []), [query]);

  useEffect(() => { setActiveIdx(0); }, [query]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function go(path: string) {
    setOpen(false);
    setQuery("");
    onNavigate?.();
    // Path may contain hash or query string
    if (path.startsWith("http")) { window.location.href = path; return; }
    if (path.includes("#") || path.includes("?")) { window.location.href = path; return; }
    void navigate({ to: path });
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    const best = bestExactHit(q);
    if (best) { go(best.path); return; }
    onNavigate?.();
    setOpen(false);
    setQuery("");
    void navigate({ to: "/search", search: { q } });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || hits.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, hits.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && hits[activeIdx]) {
      e.preventDefault();
      go(hits[activeIdx].path);
    } else if (e.key === "Escape") { setOpen(false); }
  }

  const isDesktop = variant === "desktop";

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <form
        onSubmit={onSubmit}
        role="search"
        className={`flex items-center gap-2 rounded-full border border-border/70 bg-muted/40 px-3 py-1.5 focus-within:border-primary focus-within:bg-background transition-colors ${isDesktop ? "" : "w-full"}`}
      >
        <svg width={isDesktop ? 16 : 14} height={isDesktop ? 16 : 14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 text-muted-foreground" aria-hidden>
          <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
        </svg>
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder ?? "Search gold, silver, bills, calculators…"}
          aria-label="Search DrHint"
          role="combobox"
          aria-autocomplete="list"
          aria-controls="dh-search-listbox"
          aria-expanded={open && hits.length > 0}
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        {isDesktop && (
          <kbd className="hidden lg:inline-flex items-center rounded border border-border/70 bg-background px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">⌘K</kbd>
        )}
      </form>

      {open && hits.length > 0 && (
        <div
          id="dh-search-listbox"
          role="listbox"
          className="absolute left-0 right-0 top-full mt-2 z-50 max-h-[70vh] overflow-y-auto rounded-xl border border-border/70 bg-popover shadow-xl ring-1 ring-black/5"
        >
          <ul className="py-1">
            {hits.map((h, i) => (
              <li key={h.path}>
                <button
                  type="button"
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => go(h.path)}
                  role="option"
                  aria-selected={i === activeIdx}
                  className={`flex w-full items-start gap-3 px-3 py-2 text-left text-sm transition-colors ${
                    i === activeIdx ? "bg-primary/10" : "hover:bg-muted/60"
                  }`}
                >
                  <span className="mt-0.5 text-lg leading-none" aria-hidden>{h.icon}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{h.title}</span>
                    <span className="block truncate text-[11px] text-muted-foreground">{h.group} · {h.path}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-border/60 px-3 py-2 text-[11px] text-muted-foreground">
            Press <kbd className="rounded border border-border px-1">↵</kbd> to open · <kbd className="rounded border border-border px-1">Esc</kbd> to close
          </div>
        </div>
      )}

      {open && query.trim().length >= 2 && hits.length === 0 && (
        <div className="absolute left-0 right-0 top-full mt-2 z-50 rounded-xl border border-border/70 bg-popover shadow-xl px-4 py-3 text-sm text-muted-foreground">
          No direct match. Press <kbd className="rounded border border-border px-1">↵</kbd> to search all pages.
        </div>
      )}
    </div>
  );
}